import { useMemo } from 'react';

import { useAccounts } from '@/store/app';
import { useBoolPref } from '@/store/prefs';
import { AccountItem } from './AccountItem';
import { SectionDivider } from './Item';

/**
 * Port of `sidebar/Accounts.tsx`: a divider, then All accounts / On budget /
 * Off budget sections plus a collapsible Closed accounts group.
 */
export function Accounts() {
  const accounts = useAccounts();
  const [showClosed, setShowClosed] = useBoolPref('ui.showClosedAccounts', false);

  const { onBudget, offBudget, closed, totals } = useMemo(() => {
    const openAccounts = accounts.filter(account => account.closed === 0);
    const on = openAccounts.filter(account => account.offbudget === 0);
    const off = openAccounts.filter(account => account.offbudget === 1);
    const sum = (list: typeof openAccounts) =>
      list.reduce((total, account) => total + account.balance, 0);
    return {
      onBudget: on,
      offBudget: off,
      closed: accounts.filter(account => account.closed === 1),
      totals: {
        all: sum(openAccounts),
        on: sum(on),
        off: sum(off),
      },
    };
  }, [accounts]);

  return (
    <div className="scrollable-dark min-h-0 flex-1 overflow-y-auto">
      <SectionDivider />
      <div className="pb-3">
        <AccountItem
          name="全部账户"
          to="/accounts"
          balance={totals.all}
          bold
          marginTop={15}
        />
        {onBudget.length > 0 && (
          <AccountItem
            name="预算内"
            to="/accounts/onbudget"
            balance={totals.on}
            bold
            titleRow
            marginTop={13}
          />
        )}
        {onBudget.map(account => (
          <AccountItem
            key={account.id}
            name={account.name}
            to={`/accounts/${account.id}`}
            balance={account.balance}
            connected={!!account.bank}
          />
        ))}
        {offBudget.length > 0 && (
          <AccountItem
            name="预算外"
            to="/accounts/offbudget"
            balance={totals.off}
            bold
            titleRow
            marginTop={13}
          />
        )}
        {offBudget.map(account => (
          <AccountItem
            key={account.id}
            name={account.name}
            to={`/accounts/${account.id}`}
            balance={account.balance}
            connected={!!account.bank}
          />
        ))}
        {closed.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowClosed(!showClosed)}
              className="mt-[13px] flex w-full items-center justify-between border-t border-sidebar-border px-2.5 py-1 text-sm font-semibold text-sidebar-header-text hover:bg-sidebar-item-background-hover"
              data-testid="sidebar-closed-accounts-toggle"
            >
              已关闭账户
              <span className="text-sidebar-text-muted">
                {showClosed ? '−' : '+'}
              </span>
            </button>
            {showClosed &&
              closed.map(account => (
                <AccountItem
                  key={account.id}
                  name={account.name}
                  to={`/accounts/${account.id}`}
                  balance={account.balance}
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
}
